import { startOfDay, parseISO, isToday, isTomorrow } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { create } from 'zustand';

import { supabase } from '@/common/config/supabase';
import { withAuthUser } from '@/common/config/supabaseMiddleware';
import handleException from '@/common/utils/errors';
import { showErrorToast } from '@/common/utils/toast';

// Get user's timezone
const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  event_date: Date;
  created_at: string;
  widget_id: string;
}

interface EventInput {
  title: string;
  description: string;
  event_date: Date;
}

interface CalendarState {
  events: CalendarEvent[];
  loading: boolean;
  fetchEvents: (widgetId: string) => Promise<void>;
  addEvent: (widgetId: string, event: EventInput) => Promise<void>;
  deleteEvent: (id: string) => Promise<void>;
  getTodayEvents: () => CalendarEvent[];
  getTomorrowEvents: () => CalendarEvent[];
}

export const useCalendarStore = create<CalendarState>((set, get) => ({
  events: [],
  loading: false,

  fetchEvents: async (widgetId: string) => {
    set({ loading: true });
    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('calendar_events')
          .select('*')
          .eq('user_id', userId)
          .eq('widget_id', widgetId);

        if (error) throw error;

        const events = (data || []).map((event) => ({
          ...event,
          // Convert UTC date from DB to user's timezone
          event_date: toZonedTime(parseISO(event.event_date), userTimeZone),
        }));

        set({ events });
      });
    } catch (error) {
      handleException(error);
      showErrorToast(
        'Failed to load events',
        'Could not load your calendar events. Please try again.',
      );
    } finally {
      set({ loading: false });
    }
  },

  addEvent: async (widgetId: string, event) => {
    // Convert local date to UTC before saving
    const utcDate = new Date(startOfDay(event.event_date));

    const optimisticEvent: CalendarEvent = {
      id: crypto.randomUUID(),
      ...event,
      event_date: event.event_date, // Keep local date for UI
      created_at: new Date().toISOString(),
      widget_id: widgetId,
    };

    set((state) => ({ events: [...state.events, optimisticEvent] }));

    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('calendar_events')
          .insert([
            {
              title: event.title,
              description: event.description,
              event_date: utcDate.toISOString().split('T')[0], // Save UTC date to DB
              user_id: userId,
              widget_id: widgetId,
            },
          ])
          .select()
          .single();

        if (error) throw error;
        if (data) {
          set((state) => ({
            events: state.events.map((e) =>
              e.id === optimisticEvent.id
                ? {
                    ...data,
                    event_date: toZonedTime(parseISO(data.event_date), userTimeZone),
                  }
                : e,
            ),
          }));
        }
      });
    } catch (error) {
      set((state) => ({
        events: state.events.filter((e) => e.id !== optimisticEvent.id),
      }));
      handleException(error);
      showErrorToast('Failed to add event', 'The event could not be added. Please try again.');
    }
  },

  deleteEvent: async (id: string) => {
    const previousEvents = get().events;

    set((state) => ({
      events: state.events.filter((event) => event.id !== id),
    }));

    try {
      await withAuthUser(async (userId) => {
        const { error } = await supabase
          .from('calendar_events')
          .delete()
          .match({ id, user_id: userId });

        if (error) throw error;
      });
    } catch (error) {
      set({ events: previousEvents });
      handleException(error);
      showErrorToast('Failed to delete event', 'The event could not be deleted. Please try again.');
    }
  },

  getTodayEvents: () => {
    const events = get().events;
    return events.filter((event) => isToday(event.event_date));
  },

  getTomorrowEvents: () => {
    const events = get().events;
    return events.filter((event) => isTomorrow(event.event_date));
  },
}));
